/**
 * Salvataggio automatico riga per riga: presenze dell'appello (V14), voti
 * dell'allenamento (V15) e della partita (V27), log della seduta (V67).
 *
 * A bordo campo la rete va e viene, e i tocchi arrivano piu in fretta delle
 * risposte. Tre regole:
 *
 *  - una richiesta per riga alla volta: se si tocca mentre la precedente e in
 *    viaggio, si ricorda solo l'ultimo valore voluto e lo si manda dopo, cosi
 *    una risposta lenta non riporta indietro la selezione;
 *  - un errore di rete o del server si ritenta da solo, qualche volta, con
 *    attese crescenti; poi la riga resta "non salvata" con il suo Riprova;
 *  - un rifiuto (allenamento annullato, partita non conclusa, giocatore non
 *    in rosa o che non ha giocato) non si ritenta: la riga torna all'ultimo
 *    valore salvato e il messaggio si mostra.
 *
 * Nulla si conserva sul telefono: chiusa la pagina, cio che non e partito
 * e perso, e la vista lo avvisa finche ci sono righe in sospeso.
 */

import type { StatoPresenza } from '@calcio/shared';
import { ErroreApi } from '../api/client.js';

/** Il valore di una riga: null e "non rilevato" o "non valutato". */
export type Voluto<V = StatoPresenza> = V | null;
export type EsitoRiga = 'salvato' | 'invio' | 'non_salvato';

interface Riga<V> {
  voluto: Voluto<V>;
  salvato: Voluto<V>;
  inVolo: boolean;
  tentativi: number;
  fallito: boolean;
  timer: ReturnType<typeof setTimeout> | null;
}

export const ATTESE_MS = [1000, 3000, 8000];

/** Rete assente o server in difficolta: vale la pena ritentare. */
const transitorio = (e: unknown) => !(e instanceof ErroreApi) || e.stato >= 500;

export class CodaRighe<V extends string | number = StatoPresenza> {
  private readonly righe = new Map<string, Riga<V>>();

  constructor(
    iniziali: Record<string, Voluto<NoInfer<V>>>,
    private readonly invia: (giocatoreId: string, valore: Voluto<V>) => Promise<void>,
    private readonly avvisa: (evento: { rifiuto?: string }) => void,
    private readonly attese: readonly number[] = ATTESE_MS,
  ) {
    for (const [id, stato] of Object.entries(iniziali)) this.righe.set(id, nuovaRiga(stato));
  }

  private riga(id: string): Riga<V> {
    let r = this.righe.get(id);
    if (!r) {
      r = nuovaRiga<V>(null);
      this.righe.set(id, r);
    }
    return r;
  }

  /** Il valore che la riga mostra: l'ultimo voluto, salvato o no. */
  valore(id: string): Voluto<V> {
    return this.riga(id).voluto;
  }

  esito(id: string): EsitoRiga {
    const r = this.riga(id);
    if (r.voluto === r.salvato && !r.inVolo) return 'salvato';
    return r.fallito ? 'non_salvato' : 'invio';
  }

  /** Righe con un valore scelto e non ancora confermato dal server. */
  inSospeso(): number {
    let n = 0;
    for (const r of this.righe.values()) if (r.voluto !== r.salvato || r.inVolo) n++;
    return n;
  }

  imposta(id: string, valore: Voluto<V>): void {
    const r = this.riga(id);
    r.voluto = valore;
    this.riparti(id, r);
  }

  riprova(): void {
    for (const [id, r] of this.righe) if (r.fallito) this.riparti(id, r);
  }

  /**
   * Valori dal server, come le presenze dopo "tutti presenti": le righe ferme
   * li prendono, quelle con un tocco in sospeso tengono il tocco, che partira
   * comunque.
   */
  allinea(valori: Record<string, V>): void {
    for (const [id, r] of this.righe) {
      if (r.inVolo) continue;
      const dalServer = valori[id] ?? null;
      if (r.voluto === r.salvato) r.voluto = dalServer;
      r.salvato = dalServer;
      if (r.voluto !== r.salvato) this.riparti(id, r);
    }
    this.avvisa({});
  }

  /** Ferma i ritentativi programmati, quando la vista si chiude. */
  chiudi(): void {
    for (const r of this.righe.values()) if (r.timer) clearTimeout(r.timer);
  }

  private riparti(id: string, r: Riga<V>): void {
    if (r.timer) clearTimeout(r.timer);
    r.timer = null;
    r.tentativi = 0;
    r.fallito = false;
    void this.avvia(id, r);
    this.avvisa({});
  }

  private async avvia(id: string, r: Riga<V>): Promise<void> {
    if (r.inVolo || r.timer || r.voluto === r.salvato) return;
    const valore = r.voluto;
    r.inVolo = true;
    this.avvisa({});
    let rifiuto: string | undefined;
    try {
      await this.invia(id, valore);
      r.salvato = valore;
      r.tentativi = 0;
    } catch (e) {
      if (!transitorio(e)) {
        rifiuto = e instanceof Error ? e.message : 'Modifica non salvata.';
        // Se nel frattempo si e toccato altro, quel tocco merita il suo invio.
        if (r.voluto === valore) r.voluto = r.salvato;
      } else if (r.tentativi < this.attese.length) {
        r.timer = setTimeout(() => {
          r.timer = null;
          void this.avvia(id, r);
        }, this.attese[r.tentativi]);
        r.tentativi++;
      } else {
        r.fallito = true;
      }
    } finally {
      r.inVolo = false;
    }
    if (!r.fallito && !r.timer && r.voluto !== r.salvato) void this.avvia(id, r);
    this.avvisa({ rifiuto });
  }
}

const nuovaRiga = <V>(valore: Voluto<V>): Riga<V> => ({
  voluto: valore,
  salvato: valore,
  inVolo: false,
  tentativi: 0,
  fallito: false,
  timer: null,
});
